import { z } from 'zod';
import { colourSchema } from './classification';
/**
 * Tags: the label layer.
 *
 * A tag is what a category cannot be - many per record - and that is both its
 * value and its limit. "Diwali campaign" and "Reimbursable" can both be true of
 * one expense; "Payroll" and "Software" cannot. So tags filter and annotate,
 * and they never drive a figure presented as a breakdown. See `tags` in
 * schema.ts for the full argument.
 */
/**
 * Which kinds of record can carry a tag.
 *
 * An ALLOW-LIST, and read as one everywhere. `document` covers invoices,
 * proformas and credit notes because `documents` is one table with a doc_type
 * discriminator; a `recordType` of 'invoice' would be a second, divergent copy
 * of that vocabulary.
 *
 * Payments are deliberately absent. A payment is an ALLOCATION - one payment
 * can settle three invoices carrying three different tags - so a tag on the
 * payment would be a fourth, contradicting opinion about the same money.
 */
export const RECORD_TAG_TYPES = ['expense', 'document'];
/**
 * Collapse internal whitespace as well as trimming, IN THE SCHEMA.
 *
 * "Diwali  campaign" and "Diwali campaign" are the same tag to a reader and two
 * rows to the unique index, which splits every total between them. Normalising
 * here rather than in the service means the SPA, an API-key integration and a
 * future import all get identical treatment without three copies of the rule.
 */
const tagName = z
    .string()
    .trim()
    .transform((v) => v.replace(/\s+/g, ' '))
    .pipe(z.string().min(1, 'Give the tag a name').max(60));
export const createTagSchema = z
    .object({
    name: tagName,
    colour: colourSchema.optional(),
    description: z.string().trim().max(200).optional(),
})
    .strict();
export const updateTagSchema = z
    .object({
    name: tagName.optional(),
    colour: colourSchema.optional(),
    description: z.string().trim().max(200).nullable().optional(),
    /** `false` archives. There is no separate archive endpoint to forget. */
    isActive: z.boolean().optional(),
})
    .strict();
export const listTagsQuerySchema = z
    .object({
    q: z.string().trim().max(200).optional(),
    includeInactive: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => v === true || v === 'true')
        .default(false),
    /**
     * A capped slab, not a cursor. Tags are bounded by what a business actually
     * has, like items and bank accounts; an org that reaches 500 is a product
     * conversation, not a pagination one.
     */
    limit: z.coerce.number().int().min(1).max(500).default(500),
})
    .strict();
/**
 * Replace the whole set of tags on a record.
 *
 * PUT of the set rather than POST/DELETE per tag, for two reasons. The set is
 * idempotent, so a retry is safe. And add/remove has a lost-update race - two
 * writers each adding one tag, each having read the current set, one write
 * winning - whereas replacing makes the last writer's intent the stored state,
 * which is at least a state a human chose.
 *
 * The cap is ten. Beyond that a chip row is unreadable at any width, and the
 * duplicate strip is here rather than in the service so the same id sent twice
 * cannot reach the unique index and 409 a request that meant nothing wrong.
 */
export const setRecordTagsSchema = z
    .object({
    tagIds: z
        .array(z.string().trim().min(1).max(36))
        .max(10, 'You can put ten tags on a record')
        .transform((ids) => Array.from(new Set(ids))),
})
    .strict();
/** Move a record onto a project, or off one. `null` clears it. */
export const setRecordProjectSchema = z
    .object({
    projectId: z.string().trim().max(36).nullable(),
})
    .strict();
/**
 * How a multi-tag filter combines.
 *
 * `any` is the default because the common question is "show me anything to do
 * with Diwali", not "show me the rows carrying both of these".
 */
export const TAG_MATCH_MODES = ['any', 'all'];
//# sourceMappingURL=tags.js.map